import type { ReactNode } from 'react';

import { ParrotLogo } from '@experimental-components/IconsComponents/ParrotLogo';

import type { AccountRequirement } from '@identity/domain/types';
import { ActivationStepList } from '@identity/presentation/components/base/ActivationLayout/ActivationStepList';
import type { ActivationStepItem } from '@identity/presentation/components/base/ActivationLayout/ActivationStepList';

export type ActivationLayoutProps = {
  children: ReactNode;
  currentStep: AccountRequirement | 'DONE';
  headline: string;
  steps: readonly ActivationStepItem[];
};

/**
 * Two-column layout of the invitation flow: brand panel on the left, step card on the right.
 *
 * The logo of the content column keeps the same size and alignment on every step, so the brand never
 * moves while the card below it changes. Below `lg` the brand panel is hidden and the progress list
 * moves above the card in its compact variant.
 *
 * @param {ActivationLayoutProps} props - Component properties
 */
export const ActivationLayout = ({ children, currentStep, headline, steps }: ActivationLayoutProps) => (
  <div className="flex min-h-screen w-full font-sans">
    <aside
      className="hidden w-[56%] flex-col justify-between bg-primary p-12 lg:flex"
      data-testid="activation-layout-panel"
    >
      <ParrotLogo aria-hidden className="h-10 text-primary-foreground" variant="mono" />
      <div className="flex flex-col gap-8">
        <h2 className="max-w-[440px] text-3xl font-semibold text-primary-foreground">{headline}</h2>
        <ActivationStepList currentStep={currentStep} steps={steps} />
      </div>
    </aside>

    <main className="flex flex-1 flex-col items-center justify-center gap-6 px-4 py-6 md:px-16 md:py-10">
      <ParrotLogo aria-hidden className="h-16 self-start" />
      <div className="w-full lg:hidden">
        <ActivationStepList currentStep={currentStep} steps={steps} variant="compact" />
      </div>
      {children}
    </main>
  </div>
);
